import type * as ContractTypes from '@draftkings/dk-data-layer';
import { IDataIndex, Localization, OddsStyles, SelectionHandlerIDs } from '@draftkings/component-builder';
import { MarketBuilderConfig, MarketWithSelections, marketCellsBuilder } from '@draftkings/component-builder';
import { parseScoreboards } from '@draftkings/scoreboards';
import { ParsedEvent } from '../../../contracts';
import { getDefaultValue } from '@draftkings/sportsbook-common-utils';
import {
    FeatureFlags,
    LocalizationConfig,
    Logos,
    LogosMap,
    ProductConfig
} from '@draftkings/sportsbook-common-contracts';
import { getTeamsScore } from '../getTeamScore';
import { shouldSkipParseScoreboards } from '../skipParseScoreboards';
import { NavLinkProps } from '../../../../types/window';
import { dict, isParlayCard } from '../../../helpers';
import { getPartialParsedInputs } from '../getPartialParsedInputs/getPartialParsedInputs';
import { buildSGPNavLink } from '../buildSGPNavLink';
import { getRenderNavLink } from '../getRenderNavLink/getRenderNavLink';
import { buildEventNavLink } from '../buildEventNavLink';

export type ParseEventArgs = {
    event: ContractTypes.SportEvent;
    marketsWithSelectionsByEventId: Record<string, MarketWithSelections[]>;
    isTeamSwap: boolean;
    localization: Localization;
    loadingSelectionIndex: IDataIndex<string, boolean>;
    selectedSelectionIndex: IDataIndex<string, boolean>;
    oddsStyle: OddsStyles;
    logosMap: LogosMap;
    leagueName: string;
    staticS3Host: string;
    collapsedEventIds: Set<string>;
    featureFlags: FeatureFlags;
    productConfig: ProductConfig;
    subcategoryId: string;
    componentId: number;
    logos: Logos;
    onSelectionClick: (selectionIds: SelectionHandlerIDs) => void;
    logError: MarketBuilderConfig['logError'];
    renderNavLink: (props: NavLinkProps) => JSX.Element;
    localizationConfig: LocalizationConfig;
    locale: string;
};

export const parseEvent = ({
    event,
    marketsWithSelectionsByEventId,
    isTeamSwap,
    localization,
    oddsStyle,
    logosMap,
    leagueName,
    staticS3Host,
    loadingSelectionIndex,
    selectedSelectionIndex,
    featureFlags,
    productConfig,
    subcategoryId,
    componentId,
    logos,
    collapsedEventIds,
    localizationConfig,
    onSelectionClick,
    logError,
    renderNavLink,
    locale
}: ParseEventArgs): ParsedEvent => {
    const isParlayCardComponent = isParlayCard(componentId, marketsWithSelectionsByEventId[event.id] || [], isTeamSwap);
    const skipParseScoreboards = shouldSkipParseScoreboards(isParlayCardComponent);

    const renderEventNavLink = getRenderNavLink(event, renderNavLink, buildEventNavLink);
    const renderSGPNavLink = getRenderNavLink(event, renderNavLink, buildSGPNavLink);
    const partialParsedInputs = getPartialParsedInputs({
        event,
        isTeamSwap,
        logosMap,
        staticS3Host,
        productConfig,
        featureFlags,
        renderEventNavLink,
        renderSGPNavLink,
        isParlayCardComponent,
        localizationConfig,
        locale
    });

    return {
        ...event,
        leagueName,
        scoreboardData: !skipParseScoreboards
            ? parseScoreboards({
                  ...partialParsedInputs.marketData,
                  event,
                  logosMap,
                  staticS3Host,
                  isTeamSwap,
                  swooshBaseUrl: productConfig.swooshBaseUrl,
                  isSwooshTeamLogosEnabled: featureFlags['RLS-OSB-WEB-IsSwooshTeamLogosEnabled']['LivePage'],
                  renderEventNavLink,
                  sgpBadgeData: {
                      tags: event.tags || [],
                      renderSGPNavLink
                  }
              })
            : undefined,
        parsedMarkets: {
            isExpanded: !collapsedEventIds.has(event.id),
            marketsCells: marketCellsBuilder({
                eventId: event.id,
                eventStatus: event.status,
                markets: marketsWithSelectionsByEventId[event.id] || [],
                categoryId: '',
                subCategoryId: subcategoryId,
                componentId,
                localization,
                loadingSelectionIndex,
                selectedSelectionIndex,
                oddsStyle,
                isTeamSwap,
                participants: getDefaultValue(event.participants?.slice(), []),
                featureFlags,
                consumerPageName: 'LivePage',
                renderEventNavLink,
                renderSGPNavLink,
                parsedEventState: partialParsedInputs?.marketData?.parsedEventState,
                tags: partialParsedInputs?.marketData?.tags,
                startEventDate: partialParsedInputs?.marketData?.startEventDate,
                logos,
                staticS3Host,
                logError,
                onSelectionClick,
                teamsScore: getTeamsScore(event, skipParseScoreboards),
                productConfig,
                componentType: isParlayCardComponent ? 'live' : undefined,
                eventScorecard: event?.eventScorecard,
                sportId: event?.sportId,
                dict: localizationConfig?.livePage || dict,
                localizationConfig
            } as unknown as MarketBuilderConfig)
        },
        isParlayCardComponent: isParlayCardComponent,
        headerData: {
            ...partialParsedInputs.headerData
        }
    };
};
